import { useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import {
  Badge,
  Input,
  OutlineDangerButton,
  PrimaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AuditPanel } from '~/components/common/AuditPanel'
import { CompactCivilDateField } from '~/components/common/CompactCivilDateField'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { DsSelect, type DsSelectOptions } from '~/components/common/DsSelect'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import {
  type CivilDate,
  civilDateEquals,
  compareCivilDate,
} from '~/models/civilDate'
import type { VoltageClass } from '~/models/voltageBreakdown'
import {
  FormGrid,
  GridLabel,
  ReadOnlyRow,
} from '~/pages/CustomerInfo/components/FormField'
import { FormModalShell } from '~/pages/CustomerInfo/components/FormModalShell'
import {
  KwPeriodsEditor,
  type KwRow,
} from '~/pages/CustomerInfo/components/KwPeriodsEditor'
import { LostPointModal } from '~/pages/CustomerInfo/components/LostPointModal'
import {
  useConvertProspectToContract,
  useDeleteSupplyPoint,
  useGetDeletability,
  useGetFormOptions,
  useMarkProspectLost,
  useReloadCustomerInfo,
  useUpdateSupplyPoint,
} from '~/pages/CustomerInfo/hooks/useCustomerInfoQueries'
import {
  VOLTAGE_CLASSES,
  VOLTAGE_I18N_KEY,
} from '~/pages/CustomerInfo/models/display'
import { compareKw } from '~/pages/CustomerInfo/models/kwInput'
import type { KwPeriod } from '~/pages/CustomerInfo/models/kwPeriods'
import {
  isInvalidKwInput,
  parseKwInput,
  resolveSupplyPointError,
} from '~/pages/CustomerInfo/models/resolveSupplyPointError'
import { SupplyPointError } from '~/pages/CustomerInfo/models/supplyPointError'
import type { SupplyPoint } from '~/pages/CustomerInfo/models/types'

type Props = {
  point: SupplyPoint
  onClose: () => void
}

type Pending = 'save' | 'convert' | 'lost' | 'delete' | undefined
type Confirm = 'convert' | 'lost' | 'delete' | 'conflict' | undefined

const RETAILER_UNDECIDED = '__undecided__'

/**
 * 契約編集モーダル。 失注は閲覧のみ ([[LostPointModal]])。
 * 契約: 契約kW・供給開始日・供給終了日・エリア・電圧階級を変えられ、 供給地点マスタに載らず託送実績も無い間は
 * 供給地点特定番号も直せる (削除できる条件と同じ。 条件外は表示のみ)。 見込み: 加えて番号を入れる・直す・消すことができ、
 * Footer から「失注にする」「契約に切り替える」(確認ダイアログ付き) ができる。 削除は Footer 左端 → 確認ダイアログ。
 */
export const EditContractModal = ({
  point,
  onClose,
}: Props): React.ReactElement => {
  if (point.stage === 'lost') {
    return <LostPointModal onClose={onClose} point={point} />
  }
  return <EditableContractModal onClose={onClose} point={point} />
}

const EditableContractModal = ({
  point,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const reload = useReloadCustomerInfo()
  const isProspect = point.stage === 'prospect'

  const update = useUpdateSupplyPoint()
  const convert = useConvertProspectToContract()
  const markLost = useMarkProspectLost()
  const remove = useDeleteSupplyPoint()

  const [isDirty, setIsDirty] = useState(false)
  const initialRows = (): KwRow[] =>
    point.kwPeriods.slice(1).map((p, i) => ({
      key: `stored-${i}`,
      start: p.start,
      kw: p.kw,
    }))
  const [kw, setKw] = useState(point.kwPeriods[0]?.kw ?? point.contractKw)
  const [kwRows, setKwRows] = useState<KwRow[]>(initialRows)
  const [start, setStart] = useState<CivilDate | undefined>(
    point.supplyStartDate,
  )
  const [end, setEnd] = useState<CivilDate | undefined>(point.supplyEndDate)
  const [number, setNumber] = useState(point.supplyPointNumber ?? '')
  const [areaCode, setAreaCode] = useState<AreaCode>(point.areaCode)
  const [voltage, setVoltage] = useState<VoltageClass>(point.voltageClass)
  const [retailerCode, setRetailerCode] = useState<string | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [pending, setPending] = useState<Pending>(undefined)
  const [confirm, setConfirm] = useState<Confirm>(undefined)

  // エリアを直した時と、 小売が空の見込みだけ小売を選べる (契約に切り替える時にその時点の選択肢で決める)
  const areaChanged = areaCode !== point.areaCode
  const voltageChanged = voltage !== point.voltageClass
  const retailerEditable =
    areaChanged || (isProspect && point.retailerBusinessCode === undefined)
  const { data: formOptions } = useGetFormOptions(areaCode, retailerEditable)
  const retailerOptions = formOptions?.retailerOptions ?? []
  // エリアを直して、 元の小売が新しいエリアにもいる時は、 契約も見込みも小売は変わらない (R58。 別の小売は backend が拒否する)
  const originalRetailerOffered =
    areaChanged &&
    point.retailerBusinessCode !== undefined &&
    retailerOptions.some((r) => r.businessCode === point.retailerBusinessCode)
  const showRetailerSelect =
    retailerEditable && retailerOptions.length > 0 && !originalRetailerOffered
  // 契約でエリアを直す時の小売: 利用者が選んだもの。 選ぶ前は、 今の小売が新しいエリアにあればそれ (変わらない)、
  // 無ければ空欄のまま (先頭の候補を勝手に当てない。 1 社だけのエリアでも選んでもらう)
  const contractRetailer = originalRetailerOffered
    ? point.retailerBusinessCode
    : retailerCode
  // 見込みでエリアを直す時の小売 (backend の ResolveRetailerForAreaChange と同じ規則): 今の小売が新しいエリアにあればそれが残り、
  // 無ければ利用者が選んだもの、 選ばなければ外れる (見込みは小売が未定でもよい)。 画面に出す値と送る値をここで一本にする
  const prospectRetailer = originalRetailerOffered
    ? point.retailerBusinessCode
    : retailerCode
  const savedRetailer = isProspect ? prospectRetailer : contractRetailer
  // 契約でエリアを直す時は、 小売が決まるまで保存できない (候補が取れるまでも待つ)。
  // 小売のある見込みも、 残るか外れるかが候補で決まるので、 候補が取れるまで待つ
  const optionsAwaited =
    areaChanged &&
    formOptions === undefined &&
    (!isProspect || point.retailerBusinessCode !== undefined)
  const contractNeedsPick =
    !isProspect &&
    areaChanged &&
    retailerOptions.length > 0 &&
    contractRetailer === undefined
  const retailerPending = optionsAwaited || contractNeedsPick

  // 契約は開いた時に削除できるかを取得する。 見込みは常に削除できる。
  const deletability = useGetDeletability(point.id, !isProspect)

  const areaOptions: DsSelectOptions = AREA_CODE_OPTIONS.map((code) => ({
    value: String(code),
    label: convertAreaLabel(code),
  }))
  const voltageOptions: DsSelectOptions = VOLTAGE_CLASSES.map((v) => ({
    value: v,
    label: t(VOLTAGE_I18N_KEY[v]),
  }))
  const retailerSelectOptions: DsSelectOptions = [
    ...(isProspect
      ? [
          {
            value: RETAILER_UNDECIDED,
            label: t('page:customerInfo.form.retailerUndecided'),
          },
        ]
      : []),
    ...retailerOptions.map((r) => ({
      value: r.businessCode,
      label: r.businessName,
    })),
  ]

  const touch = <T,>(setter: (value: T) => void) => {
    return (value: T): void => {
      setIsDirty(true)
      setError(undefined)
      setter(value)
    }
  }

  const token = {
    supplyPointId: point.id,
    expectedUpdatedAt: point.version,
  }

  const showError = (err: unknown): void => {
    const resolved = resolveSupplyPointError(err, t)
    if (resolved.reason === 'CONFLICT') {
      setConfirm('conflict')
      return
    }
    setError(resolved.message)
  }

  const requireStart = (): CivilDate | undefined => {
    if (start === undefined) {
      setError(t('page:customerInfo.form.supplyStartRequired'))
    }
    return start
  }

  // 最初の期間より後の期間: 変えていなければ送らない。 入力の欠けは 'incomplete'
  const collectKwPeriods = (): KwPeriod[] | undefined | 'incomplete' => {
    const complete: KwPeriod[] = []
    for (const r of kwRows) {
      const value = parseKwInput(r.kw)
      if (r.start === undefined || value === undefined) return 'incomplete'
      complete.push({ start: r.start, kw: value })
    }
    // 日付順に 1 対 1 で突き合わせる (行の並びは問わない。 B を A と同じ内容に直した重複を「変更なし」と見ない)
    const byStart = (a: KwPeriod, b: KwPeriod): number =>
      compareCivilDate(a.start, b.start) || compareKw(a.kw, b.kw)
    const stored = point.kwPeriods.slice(1).sort(byStart)
    const sorted = [...complete].sort(byStart)
    const same =
      sorted.length === stored.length &&
      sorted.every(
        (c, i) =>
          civilDateEquals(stored[i].start, c.start) && stored[i].kw === c.kw,
      )
    return same ? undefined : complete
  }

  const handleSave = (): void => {
    void (async () => {
      setError(undefined)
      const supplyStartDate = requireStart()
      if (supplyStartDate === undefined) return
      if (isInvalidKwInput(kw)) {
        setError(
          resolveSupplyPointError(
            new SupplyPointError('CONTRACT_KW_NOT_POSITIVE'),
            t,
          ).message,
        )
        return
      }
      const trimmed = number.trim()
      const kwPeriods = collectKwPeriods()
      if (kwPeriods === 'incomplete') {
        setError(t('page:customerInfo.kwPeriod.incomplete'))
        return
      }
      setPending('save')
      try {
        await update.mutateAsync({
          supplyPointId: point.id,
          contractKw: parseKwInput(kw),
          kwPeriods,
          supplyStartDate,
          supplyEndDate: end,
          prospectNumber:
            isProspect && trimmed !== (point.supplyPointNumber ?? '')
              ? trimmed
              : undefined,
          supplyPointNumber:
            !isProspect &&
            numberEditable &&
            trimmed !== (point.supplyPointNumber ?? '')
              ? trimmed
              : undefined,
          areaCode: areaChanged ? areaCode : undefined,
          voltageClass: voltageChanged ? voltage : undefined,
          retailerBusinessCode: areaChanged ? savedRetailer : undefined,
          expectedUpdatedAt: point.version,
        })
        showSuccessToast({ title: t('page:customerInfo.toast.updated') })
        onClose()
      } catch (err) {
        showError(err)
      } finally {
        setPending(undefined)
      }
    })()
  }

  // 「契約に切り替える」: 入力の欠けを確かめてから、 戻せないことを確認ダイアログで伝える
  const handleConvertClick = (): void => {
    setError(undefined)
    if (areaChanged || voltageChanged) {
      setError(t('page:customerInfo.modal.saveBeforeConvert'))
      return
    }
    if (requireStart() === undefined) return
    setConfirm('convert')
  }

  const handleConvert = (): void => {
    void (async () => {
      if (start === undefined) return
      const kwPeriods = collectKwPeriods()
      if (kwPeriods === 'incomplete') {
        setConfirm(undefined)
        setError(t('page:customerInfo.kwPeriod.incomplete'))
        return
      }
      setPending('convert')
      try {
        await convert.mutateAsync({
          supplyPointId: point.id,
          supplyPointNumber: number.trim(),
          retailerBusinessCode: showRetailerSelect ? retailerCode : undefined,
          contractKw: parseKwInput(kw),
          supplyStartDate: start,
          supplyEndDate: end,
          kwPeriods,
          expectedUpdatedAt: point.version,
        })
        showSuccessToast({ title: t('page:customerInfo.toast.converted') })
        onClose()
      } catch (err) {
        setConfirm(undefined)
        showError(err)
      } finally {
        setPending(undefined)
      }
    })()
  }

  const handleMarkLost = (): void => {
    void (async () => {
      setPending('lost')
      try {
        await markLost.mutateAsync(token)
        showSuccessToast({ title: t('page:customerInfo.toast.lost') })
        onClose()
      } catch (err) {
        setConfirm(undefined)
        showError(err)
      } finally {
        setPending(undefined)
      }
    })()
  }

  const handleDelete = (): void => {
    void (async () => {
      setPending('delete')
      try {
        await remove.mutateAsync(token)
        showSuccessToast({ title: t('page:customerInfo.toast.deleted') })
        onClose()
      } catch (err) {
        setConfirm(undefined)
        showError(err)
      } finally {
        setPending(undefined)
      }
    })()
  }

  const numberLabel =
    point.supplyPointNumber ?? t('page:customerInfo.delete.numberUndecided')
  const none = t('page:customerInfo.common.none')

  const deletable = isProspect || deletability.data?.deletable === true
  // 契約の番号は、 削除できる条件と同じ間だけ直せる (開くまで・取れない間は表示のみ)
  const numberEditable = !isProspect && deletability.data?.deletable === true
  const deleteBlocked =
    !isProspect &&
    deletability.data !== undefined &&
    !deletability.data.deletable
  const isBusy = pending !== undefined

  return (
    <>
      <FormModalShell
        errorMessage={error}
        footerActions={
          isProspect ? (
            <>
              <OutlineDangerButton
                disabled={isBusy}
                onClick={() => setConfirm('lost')}
                title={t('page:customerInfo.action.markLostHint')}
              >
                {t('page:customerInfo.action.markLost')}
              </OutlineDangerButton>
              <PrimaryButton
                disabled={isBusy}
                loading={pending === 'convert'}
                onClick={handleConvertClick}
                title={t('page:customerInfo.action.convertHint')}
              >
                {t('page:customerInfo.action.convert')}
              </PrimaryButton>
            </>
          ) : undefined
        }
        footerStart={
          deletable ? (
            <OutlineDangerButton
              disabled={isBusy}
              onClick={() => setConfirm('delete')}
            >
              {t('common:action.delete')}
            </OutlineDangerButton>
          ) : undefined
        }
        isDirty={isDirty}
        isPending={pending === 'save'}
        isSaveDisabled={retailerPending}
        onClose={onClose}
        onSave={handleSave}
        title={t('page:customerInfo.modal.editTitle')}
      >
        <FormGrid>
          <ReadOnlyRow label={t('page:customerInfo.form.customerName')}>
            {point.customerName}
          </ReadOnlyRow>

          {isProspect && (
            <ReadOnlyRow label={t('page:customerInfo.form.stage')}>
              <Badge status="info">
                {t('page:customerInfo.status.prospect')}
              </Badge>
            </ReadOnlyRow>
          )}

          {!isProspect &&
            (numberEditable ? (
              <>
                <GridLabel htmlFor="customer-info-edit-contract-number">
                  {t('page:customerInfo.form.supplyPointNumber')}
                </GridLabel>
                <Input
                  id="customer-info-edit-contract-number"
                  inputMode="numeric"
                  maxLength={22}
                  onChange={(e) => touch(setNumber)(e.target.value)}
                  value={number}
                />
              </>
            ) : (
              <ReadOnlyRow
                label={t('page:customerInfo.form.supplyPointNumber')}
              >
                {point.supplyPointNumber}
              </ReadOnlyRow>
            ))}

          <GridLabel htmlFor="customer-info-edit-area">
            {t('page:customerInfo.form.area')}
          </GridLabel>
          <DsSelect
            inputId="customer-info-edit-area"
            onChange={(value) => {
              if (value === undefined) return
              touch(setAreaCode)(Number(value) as AreaCode)
              // 小売の選択肢はエリアに追随する (別エリアの小売が残らないように)
              setRetailerCode(undefined)
            }}
            options={areaOptions}
            value={String(areaCode)}
          />

          <GridLabel htmlFor="customer-info-edit-voltage">
            {t('page:customerInfo.form.voltage')}
          </GridLabel>
          <DsSelect
            inputId="customer-info-edit-voltage"
            onChange={(value) => {
              if (value !== undefined) touch(setVoltage)(value as VoltageClass)
            }}
            options={voltageOptions}
            value={voltage}
          />

          {/* 小売は読み取り専用でも出す (同じ需要家に複数小売の地点が並ぶことがある) */}
          {showRetailerSelect ? (
            <>
              <GridLabel htmlFor="customer-info-edit-retailer">
                {t('page:customerInfo.form.retailer')}
              </GridLabel>
              <DsSelect
                inputId="customer-info-edit-retailer"
                onChange={(value) =>
                  touch(setRetailerCode)(
                    value === RETAILER_UNDECIDED ? undefined : value,
                  )
                }
                options={retailerSelectOptions}
                placeholder={t('page:customerInfo.form.select')}
                value={savedRetailer ?? RETAILER_UNDECIDED}
              />
            </>
          ) : (
            <ReadOnlyRow
              label={t('page:customerInfo.contract.column.retailer')}
            >
              {(isProspect && areaChanged
                ? savedRetailer !== undefined
                  ? point.retailerName
                  : undefined
                : point.retailerName) ?? none}
            </ReadOnlyRow>
          )}

          {isProspect && (
            <>
              <GridLabel htmlFor="customer-info-edit-number">
                {t('page:customerInfo.form.supplyPointNumberOptional')}
              </GridLabel>
              <Input
                id="customer-info-edit-number"
                inputMode="numeric"
                maxLength={22}
                onChange={(e) => touch(setNumber)(e.target.value)}
                placeholder={t(
                  'page:customerInfo.form.supplyPointNumberPlaceholder',
                )}
                value={number}
              />
            </>
          )}

          <GridLabel htmlFor="customer-info-edit-start">
            {t('page:customerInfo.form.supplyStart')}
          </GridLabel>
          <CompactCivilDateField
            id="customer-info-edit-start"
            onChange={touch(setStart)}
            value={start}
          />

          <GridLabel htmlFor="customer-info-edit-end">
            {t('page:customerInfo.form.supplyEnd')}
          </GridLabel>
          <CompactCivilDateField
            hasClear
            id="customer-info-edit-end"
            onChange={touch(setEnd)}
            value={end}
          />

          <KwPeriodsEditor
            firstKw={kw}
            label={t('page:customerInfo.form.contractKw')}
            onFirstKwChange={touch(setKw)}
            onRowsChange={touch(setKwRows)}
            rows={kwRows}
            supplyEnd={end}
            supplyStart={start}
          />
        </FormGrid>

        {deleteBlocked && (
          <Text color="Text.midEmphasis" fontSize="sm">
            {t('page:customerInfo.modal.deleteNotAllowed')}
          </Text>
        )}
        {deleteBlocked && (
          <Text color="Text.midEmphasis" fontSize="sm">
            {t('page:customerInfo.modal.numberNotEditable')}
          </Text>
        )}
        {!isProspect && deletability.isError && (
          <Text color="Text.negativeMid" fontSize="sm">
            {t('page:customerInfo.modal.deletabilityError')}
          </Text>
        )}
        <VStack align="stretch" gap={0}>
          <AuditPanel
            createdAt={point.createdAt}
            createdBy={point.createdBy}
            updatedAt={point.updatedAt}
            updatedBy={point.updatedBy}
          />
        </VStack>
      </FormModalShell>

      <ConfirmDialog
        body={t('page:customerInfo.confirm.convertBody')}
        confirmLabel={t('page:customerInfo.confirm.convertAction')}
        isLoading={pending === 'convert'}
        isOpen={confirm === 'convert'}
        onCancel={() => setConfirm(undefined)}
        onConfirm={handleConvert}
        title={t('page:customerInfo.confirm.convertTitle')}
      />
      <ConfirmDialog
        body={t('page:customerInfo.confirm.lostBody')}
        confirmLabel={t('page:customerInfo.confirm.lostAction')}
        destructive
        isLoading={pending === 'lost'}
        isOpen={confirm === 'lost'}
        onCancel={() => setConfirm(undefined)}
        onConfirm={handleMarkLost}
        title={t('page:customerInfo.confirm.lostTitle')}
      />
      <ConfirmDialog
        // #9851 で需要特性の文言を戻す (page:customerInfo.delete.bodyWithDemandProfile)
        body={t('page:customerInfo.delete.body', {
          spNumber: numberLabel,
          name: point.customerName,
        })}
        confirmLabel={t('common:action.delete')}
        destructive
        isLoading={pending === 'delete'}
        isOpen={confirm === 'delete'}
        onCancel={() => setConfirm(undefined)}
        onConfirm={handleDelete}
        title={t('page:customerInfo.delete.title')}
      />
      <ConfirmDialog
        body={t('page:customerInfo.confirm.conflictBody')}
        confirmLabel={t('page:customerInfo.confirm.conflictConfirm')}
        isOpen={confirm === 'conflict'}
        onCancel={() => setConfirm(undefined)}
        onConfirm={() => {
          void reload()
          onClose()
        }}
        title={t('page:customerInfo.confirm.conflictTitle')}
      />
    </>
  )
}
